import {
  CvSearchSyncHealthDto,
  CvSearchSyncProblemCategoryDto,
  CvSearchSyncProblemItemDto,
  CvSearchSyncStatusResponseDto,
} from '@/api/generated';
import { themeColors } from '@/config/theme/themeColors.ts';
import { RouteEnum } from '@/layouts/routes/model/Route.model.ts';
import { formatDateTime } from '@/utils/formatDate.util.ts';

const DOT_COLORS: Record<CvSearchSyncHealthDto, string> = {
  [CvSearchSyncHealthDto.Disabled]: themeColors.grey,
  [CvSearchSyncHealthDto.Green]: themeColors.green600,
  [CvSearchSyncHealthDto.Red]: themeColors.red,
  [CvSearchSyncHealthDto.Unknown]: themeColors.grey,
  [CvSearchSyncHealthDto.Yellow]: themeColors.darkYellow,
};

type TooltipText = {
  key: 'tooltip.disabled' | 'tooltip.status' | 'tooltip.unknown';
  params?: { count: number; time: string };
};

const dotColor = (health?: CvSearchSyncHealthDto): string =>
  DOT_COLORS[health ?? CvSearchSyncHealthDto.Unknown];

// A missing CV file is outside this application's control, so it is listed but not counted as a problem.
const problemsCount = (status: CvSearchSyncStatusResponseDto): number =>
  status.problems
    .filter(problem => problem.category !== CvSearchSyncProblemCategoryDto.CvMissing)
    .reduce((sum, problem) => sum + problem.count, 0);

const tooltip = (status?: CvSearchSyncStatusResponseDto): TooltipText => {
  if (!status?.computedAt) {
    return { key: 'tooltip.unknown' };
  }
  if (status.health === CvSearchSyncHealthDto.Disabled) {
    return { key: 'tooltip.disabled' };
  }
  return {
    key: 'tooltip.status',
    params: { count: problemsCount(status), time: formatDateTime(status.computedAt) },
  };
};

const candidateLabel = (item: CvSearchSyncProblemItemDto): string => {
  const name = [item.firstName, item.lastName].filter(Boolean).join(' ');
  return name || item.documentId;
};

const candidateLink = (item: CvSearchSyncProblemItemDto): string | undefined => {
  if (!item.processId || !item.candidateApplicationId) {
    return undefined;
  }
  return RouteEnum.CANDIDATE_DETAILS.replace(':processId', item.processId).replace(
    ':candidateApplicationId',
    item.candidateApplicationId,
  );
};

export const cvSearchSyncStatusUtil = {
  candidateLabel,
  candidateLink,
  dotColor,
  problemsCount,
  tooltip,
};
